import {MatchBill, MatchTransaction} from "../types.ts";
import {MoneyLabel} from "../../../../../../components/general/MoneyLabel.tsx";
import {ArrowsRightLeftIcon, DocumentTextIcon, XMarkIcon} from "@heroicons/react/24/outline";
import {formatDate, billTypeLabel} from "../utils/utils.ts";
import {useState} from "react";
import {BankAccountLogo} from "../../../../../../components/general/BankAccountLogo.tsx";
import {cleanDocumentFileName} from "../../../../../../lib/TFICLib.ts";
import {DescriptionWithTooltip} from "./DescriptionWithTooltip.tsx";

interface BackendMatchItemProps {
    bill?: MatchBill;
    transaction?: MatchTransaction;
    onDelete: () => Promise<void>;
    onOpenDocument?: () => void;
    currency: string;
}



export function PartialMatchItem({
    bill,
    transaction,
    onDelete,
    onOpenDocument,
    currency,
}: BackendMatchItemProps) {
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDelete = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isDeleting) return;
        
        setIsDeleting(true);
        try {
            await onDelete();
        } catch (error) {
            console.error("Error deleting backend match:", error);
            setIsDeleting(false);
        }
    };

    const isBill = bill !== undefined;
    const title = bill?.relation_name || transaction?.relation_name || transaction?.business_name;
    const amount = bill?.signed_amount || transaction?.signed_amount;
    const date = bill?.normalized_date || transaction?.booking_date;
    const metadata = bill ? `${bill.reference_identifier || "onbekend"} ${cleanDocumentFileName(bill.file_name)}` : transaction?.desc;
    const description = metadata || title || "";
    const logoUrl = transaction?.bank_logo_url;
    const typeLabel = bill ? billTypeLabel(bill.document_direction, bill.nrs_amount) : null;
    const TypeIcon = isBill ? DocumentTextIcon : ArrowsRightLeftIcon;

    return (
        <div className="flex items-center gap-3 border border-gray-200 rounded-full px-3 py-1 bg-white">
            <div className="flex items-center gap-2 min-w-0 flex-1">
                <TypeIcon className="size-4 shrink-0 text-gray-400" aria-hidden="true" />
                {logoUrl ? (
                    <BankAccountLogo
                        logoUrl={logoUrl}
                        className="size-5 shrink-0"
                    />
                ) : typeLabel ? (
                    <div className="shrink-0 inline-flex items-center rounded-full px-2 py-[0.1rem] text-xs font-medium bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-200">
                        {typeLabel}
                    </div>
                ) : null}
                <DescriptionWithTooltip
                    description={description}
                    onClick={onOpenDocument}
                    containerClassName="min-w-0 flex-1"
                    clampClassName="truncate"
                    textClassName="font-medium text-gray-700"
                />
            </div>

            <div className="flex items-center gap-2 shrink-0">
                {date && (
                    <span className="text-[12px] text-gray-500">
                        {formatDate(date)}
                    </span>
                )}

                {amount && (
                    <span className="text-[12px] text-gray-500">
                        <MoneyLabel value={amount.signed_amount / 100} currency={currency} colorAid={true} showCurrency={true}/>
                    </span>
                )}

                <button
                    type="button"
                    className={`p-1 rounded-full hover:bg-gray-100 text-gray-400 transition-colors ${isDeleting ? 'animate-pulse' : ''}`}
                    onClick={handleDelete}
                    disabled={isDeleting}
                    aria-label="Remove backend match"
                >
                    <XMarkIcon className="size-4" />
                </button>
            </div>
        </div>
    );
}
